import { useCallback, useLayoutEffect, useReducer, useRef, type MutableRefObject } from 'react';

/**
 * `this.state` / `this.setState` semantics for a function component.
 *
 * ## Why this exists
 *
 * `mainMenu.jsx` is a 2,850-line class component whose methods read `this.state`
 * mid-flight and call `this.setState` with partial objects, many times per handler.
 * Three properties of that model are load-bearing here, and none is what plain
 * `useState` gives you:
 *
 * 1. **Reads see the last *committed* state, not pending updates.** In the class,
 *    `componentDidUpdate` notices `operationalUserGteEmployeeNo` has changed, calls
 *    `setSelectedDelegateUser()` (which `setState`s it) and then immediately
 *    `getProjectList()` — which reads `this.state.selectedDate` and
 *    `this.props.operationalUserGteEmployeeNo`. The setState has not committed, so the
 *    fetch runs against the pre-update state. Emulating setState with a synchronously
 *    updated ref would change which week's data that request asks for.
 *
 * 2. **Updates merge shallowly and batch.** `getProjectList` issues two separate
 *    `setState` calls inside one `.then` (mainMenu.jsx:453 and :476); they coalesce into
 *    a single commit rather than the second clobbering the first.
 *
 * 3. **State objects keep their identity, so in-place mutation is visible immediately.**
 *    `getFreezeDate` does `let d = this.state.freezeDateData; d.configValue = …;
 *    d.isUpdated = true; this.setState({freezeDateData: d})` and then reads
 *    `this.state.freezeDateData.isUpdated` on the very next line — which is `true`
 *    despite the setState not having committed, because `d` *is* the object in state.
 *    That read decides whether `validateFreezeDate()` runs at all. `stateRef` holds the
 *    same object references the class did, so this keeps working; a deep clone on every
 *    commit would silently break it.
 *
 * A `useReducer`-based rewrite of the state machine would be the idiomatic React shape,
 * but it would also be a rewrite: every one of those read-then-write sequences would have
 * to be re-derived, and any that were re-derived wrong would be a silent behaviour
 * change. This migration is explicitly bug-for-bug, so the state model is reproduced
 * instead of replaced.
 *
 * ## How it works
 *
 * `stateRef.current` holds the committed state and is what `state` returns. `setState`
 * pushes a partial (or updater) onto a pending queue and schedules a re-render; the queue
 * is drained at the top of the next render, before any component reads. React's own
 * batching therefore provides the batching, and reads between a `setState` and the next
 * render see the pre-update value — exactly as in the class.
 */

export type StateUpdater<S> = Partial<S> | ((prev: S) => Partial<S>);

export interface LegacyState<S> {
  /** The committed state. Equivalent to `this.state`. */
  state: S;
  /**
   * Also the committed state, as a ref. Use this inside callbacks and promise
   * continuations, where a captured `state` would be stale — the class read `this.state`
   * there, which was always current.
   */
  stateRef: MutableRefObject<S>;
  /** Equivalent to `this.setState` — shallow merge, applied on the next render. */
  setState: (updater: StateUpdater<S>) => void;
}

export function useLegacyState<S extends object>(initial: S | (() => S)): LegacyState<S> {
  const [, forceRender] = useReducer((tick: number) => tick + 1, 0);

  const stateRef = useRef<S | null>(null);
  if (stateRef.current === null) {
    stateRef.current = typeof initial === 'function' ? (initial as () => S)() : initial;
  }

  const pendingRef = useRef<StateUpdater<S>[]>([]);

  /*
   * Drain during render, not in an effect. An effect would run after children have
   * already rendered against the previous state, giving every commit an extra frame and
   * changing what the first paint shows.
   *
   * Writing a ref during render is normally a smell, but it is idempotent here: the queue
   * is emptied as it is drained, so StrictMode's double invocation is a no-op the second
   * time through.
   */
  if (pendingRef.current.length > 0) {
    let next = stateRef.current as S;
    for (const updater of pendingRef.current) {
      const partial = typeof updater === 'function' ? updater(next) : updater;
      next = { ...next, ...partial };
    }
    pendingRef.current = [];
    stateRef.current = next;
  }

  const setState = useCallback((updater: StateUpdater<S>) => {
    pendingRef.current.push(updater);
    forceRender();
  }, []);

  return { state: stateRef.current as S, stateRef: stateRef as MutableRefObject<S>, setState };
}

/**
 * `componentDidUpdate(prevProps, prevState)` for a function component.
 *
 * Runs the callback after every committed render except the first, handing it the state
 * from the previous commit. mainMenu.jsx's `componentDidUpdate` is built almost entirely
 * out of `prevState.x !== this.state.x` reference comparisons (lines 206, 223, 226, 287),
 * so it needs the actual previous object, not a dependency array.
 *
 * `useLayoutEffect` rather than `useEffect`: componentDidUpdate fires synchronously after
 * commit and before paint, and this callback starts network requests. Deferring it past
 * paint would let a frame render against state the class never showed.
 */
export function useDidUpdate<S>(state: S, effect: (prevState: S) => void): void {
  const prevRef = useRef<S | null>(null);
  const mountedRef = useRef(false);

  const effectRef = useRef(effect);
  effectRef.current = effect;

  // No dependency array — componentDidUpdate runs after every render, and the
  // prev !== state check below is what decides whether there is anything to do.
  useLayoutEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      prevRef.current = state;
      return;
    }

    const prev = prevRef.current as S;
    prevRef.current = state;

    if (prev !== state) {
      effectRef.current(prev);
    }
  });
}

/**
 * `componentDidUpdate(prevProps, prevState)` where the callback needs BOTH.
 *
 * `useDidUpdate` above skips when the state object is unchanged, which is right for a
 * callback that only reads `prevState`. mainMenu.jsx's componentDidUpdate is not that: it
 * compares `this.props.error !== prevProps.error` (line 182),
 * `this.state.operationalUserGteEmployeeNo !== this.props.operationalUserGteEmployeeNo`
 * (line 189) and `prevProps.clickedAlert !== this.props.clickedAlert` (line 303). Those
 * branches must fire on a pure prop change, with state untouched — so this variant runs
 * whenever EITHER changed, which is what the class did.
 *
 * There is no `prev !== next` shortcut on props: the parent passes fresh inline arrow
 * props on every render (App.js:184-198), so a props object identity check would be true
 * every time anyway. The guards inside the callback are what actually decide.
 */
export function useLegacyDidUpdate<P, S>(
  props: P,
  state: S,
  effect: (prevProps: P, prevState: S) => void,
): void {
  const prevPropsRef = useRef<P | null>(null);
  const prevStateRef = useRef<S | null>(null);
  const mountedRef = useRef(false);

  const effectRef = useRef(effect);
  effectRef.current = effect;

  useLayoutEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      prevPropsRef.current = props;
      prevStateRef.current = state;
      return;
    }

    const prevProps = prevPropsRef.current as P;
    const prevState = prevStateRef.current as S;
    prevPropsRef.current = props;
    prevStateRef.current = state;

    effectRef.current(prevProps, prevState);
  });
}
